<script lang="ts">
    import tabIcon from "@/assets/gear.svg";

    export default {
        name: "GenomlysningsapparatList",
        tabName: "Genomlysningsapparat",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import CollapsibleSection from "@/components/generic/CollapsibleSection.vue";
    import { ref, onBeforeUnmount, onBeforeMount, computed } from "vue";
    import { openDialog } from "@/utils";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import KlinikenTable from "@/components/Table/KlinikenTable.vue";
    import KButton from "@/components/Button.vue";
    import GenomlysningsapparatForm from "@/tabs/Operation/Admin/Genomlysningsapparat/GenomlysningsapparatForm.vue";
    import TypAvGenomlysningsapparatList from "@/tabs/Operation/Admin/Genomlysningsapparat/TypAvGenomlysningsapparatList.vue";
    import store from "@/store";

    // TODO Ändra till användning av type i typAvGenomlysningsapparatModule i Vue3
    interface TypAvGenomlysningsapparat {
        id: number;
        namn: string;
        kod: string;
        ordning: number;
    }

    // TODO Ändra till användning av type i genomlysningsapparatModule i Vue3
    interface Genomlysningsapparat {
        id: number;
        namn: string;
        typ: number;
        typ_display?: TypAvGenomlysningsapparat;
        ordning: number;
    }

    const listPage = ref<number>(1);
    const listCount = ref<number>(0);
    const listLimit = ref<number>(20);
    const filterParams = ref<
        | {
              [key: string]: string;
          }
        | undefined
    >();

    const subsetName = ref<string>("default");
    const genomlysningsapparater = computed<Genomlysningsapparat[]>(() =>
        store.getters["genomlysningsapparat/genomlysningsapparatSubset"]({
            subsetName: subsetName.value,
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );

    const nyaGenomlysningsapparater = computed<Genomlysningsapparat[]>(() =>
        store.getters["genomlysningsapparat/genomlysningsapparatSubset"]({
            subsetName: "newlyCreated",
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );
    const redigeradeGenomlysningsapparater = computed<Genomlysningsapparat[]>(() =>
        store.getters["genomlysningsapparat/genomlysningsapparatSubset"]({
            subsetName: "newlyUpdated",
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );

    const count = ref(0);
    const columns = ref([
        {
            title: "Namn",
            name: "namn",
        },
        {
            title: "Typ",
            name: "typ_display.namn",
        },
        {
            title: "Ordning",
            name: "ordning",
        },
    ]);

    const dropdownActions = ref([
        {
            name: "editGenomlysningsapparat",
            title: "Redigera genomlysningsapparat",
            show: () => true,
        },
    ]);

    const limit = ref(50);
    const offset = ref(0);
    const fetchBatch = ref(1);
    const currentPage = ref(1);

    const loadPage = async (page: any) => {
        currentPage.value = page;
        offset.value = (page - 1) * limit.value;
        await fetchGenomlysningsapparater({ page: listPage.value });
    };

    const loadMore = async () => {
        fetchBatch.value++;
        await fetchGenomlysningsapparater({ page: listPage.value });
    };

    const changeLimit = async (dropdownLimit: any) => {
        fetchBatch.value = 1;
        limit.value = dropdownLimit;
        await fetchGenomlysningsapparater({ page: listPage.value });
    };

    const fetchGenomlysningsapparater = async ({ page, limit = listLimit.value }: { page: number; limit?: number }) => {
        try {
            const {
                nextPage,
                total,
                subsetName: fetchedSubsetName,
            } = await store.dispatch("genomlysningsapparat/fetchGenomlysningsapparater", {
                page,
                limit,
                filterParams: filterParams.value,
            });

            listPage.value = page;
            subsetName.value = fetchedSubsetName;

            if (total) {
                listCount.value = total;
            }
        } catch (_) {
            console.log(_);
            openDialog("Kunde inte hämta genomlysningsapparater", "error");
        }
    };

    const openCreateGenomlysningsapparat = () => {
        store.dispatch("tabs/openTab", {
            component: GenomlysningsapparatForm,
        });
    };

    const openTypAvGenomlysningsapparat = () => {
        store.dispatch("tabs/openTab", {
            component: TypAvGenomlysningsapparatList,
        });
    };

    const editGenomlysningsapparatFromTable = (genomlysningsapparat: Genomlysningsapparat) => {
        store.dispatch("tabs/openTab", {
            component: GenomlysningsapparatForm,
            parameters: {
                genomlysningsapparatId: genomlysningsapparat.id,
            },
        });
    };

    onBeforeMount(async () => {
        await fetchGenomlysningsapparater({ page: listPage.value });
        await store.dispatch("typAvGenomlysningsapparat/fetchTyperAvGenomlysningsapparater", {});
        await store.dispatch("genomlysningsapparat/addSubscriber");
        await store.dispatch("typAvGenomlysningsapparat/addSubscriber");
    });

    onBeforeUnmount(async () => {
        await store.dispatch("genomlysningsapparat/removeSubscriber");
        await store.dispatch("typAvGenomlysningsapparat/removeSubscriber");
    });
</script>

<template>
    <div class="genomlysningsapparat-list">
        <AdminLockBorder />
        <div class="header">
            <h1>Genomlysningsapparater</h1>
            <KButton variant="hollow" @click="openCreateGenomlysningsapparat" :style="{ padding: 0 }">
                <img src="@/assets/plus_circle_blue.svg" />
                <span>Skapa genomlysningsapparat</span>
            </KButton>
            <KButton variant="link" @click="openTypAvGenomlysningsapparat">
                <span>Typer av genomlysningsapparater</span>
            </KButton>
        </div>

        <div class="section newly-edited" v-if="redigeradeGenomlysningsapparater.length > 0">
            <CollapsibleSection isSingle :isExpanded="true" heading="Senast redigerade">
                <KlinikenTable
                    :items="redigeradeGenomlysningsapparater"
                    :columns="columns"
                    @editGenomlysningsapparat="editGenomlysningsapparatFromTable"
                    :has-dropdown="true"
                    :dropdown-actions="dropdownActions"
                />
            </CollapsibleSection>
        </div>
        <div class="section newly-created" v-if="nyaGenomlysningsapparater.length > 0">
            <CollapsibleSection isSingle :isExpanded="true" heading="Senast tillagda">
                <KlinikenTable
                    :items="nyaGenomlysningsapparater"
                    :columns="columns"
                    @editGenomlysningsapparat="editGenomlysningsapparatFromTable"
                    :has-dropdown="true"
                    :dropdown-actions="dropdownActions"
                />
            </CollapsibleSection>
        </div>

        <div class="section">
            <h3 v-if="nyaGenomlysningsapparater.length > 0 || redigeradeGenomlysningsapparater.length > 0">Alla</h3>
            <KlinikenTable
                v-if="genomlysningsapparater"
                :items="genomlysningsapparater"
                paginationStyle="paging"
                :columns="columns"
                :count="count"
                :limit="limit"
                :currentPage="currentPage"
                @loadPage="loadPage"
                @changeLimit="changeLimit"
                @loadMore="loadMore"
                :showBottom="true"
                :showLimitDropdown="true"
                :has-dropdown="true"
                :dropdown-actions="dropdownActions"
                @editGenomlysningsapparat="editGenomlysningsapparatFromTable"
            />
        </div>
    </div>
</template>

<style lang="scss">
    .genomlysningsapparat-list {
        .header {
            h1 {
                margin: 0;
            }

            display: flex;
            gap: 20px;
            align-items: center;
            flex-wrap: wrap;
        }
    }
</style>
